import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { ActivityReservation } from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./ActivityBookingQuestionsSection.module.scss";
import { useActivityBookingQuestionsSection } from "./useActivityBookingQuestionsSection";

interface ActivityBookingQuestionsSectionProps {
  reservation: ActivityReservation;
  className?: string;
}

export const ActivityBookingQuestionsSection = ({
  reservation,
  className,
}: ActivityBookingQuestionsSectionProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "ActivityBookingQuestionsSection");

  const {
    inclusions,
    exclusions,
    languageLabel,
    specialRequirements,
    hasInclusions,
    hasContent,
    startTimeLabel,
    endTimeLabel,
    pickupInfo,
    meetupLocation,
    dropOffLocation,
  } = useActivityBookingQuestionsSection(reservation);

  if (!hasContent) return null;

  return (
    <div className={clsx(block, className)}>
      <h3 className={cn("title")}>
        {t("activities.postBooking.details.title")}
      </h3>

      {(startTimeLabel || pickupInfo || meetupLocation) && (
        <div className={cn("section", ["pickup-section"])}>
          {startTimeLabel && (
            <div className={cn("field")}>
              <div className={cn("label")}>
                {t("activities.postBooking.startTime")}
              </div>
              <p className={cn("value")}>{startTimeLabel}</p>
            </div>
          )}
          {pickupInfo && (
            <div className={cn("field")}>
              <div className={cn("label")}>
                {t("activities.postBooking.details.pickup")}
              </div>
              <p className={cn("value")}>{pickupInfo.label}</p>
              {pickupInfo.location && (
                <p className={cn("value")}>{pickupInfo.location}</p>
              )}
            </div>
          )}
          {meetupLocation && (
            <div className={cn("field")}>
              <div className={cn("label")}>
                {t("activities.postBooking.details.meetupLocation")}
              </div>
              <p className={cn("value")}>{meetupLocation}</p>
            </div>
          )}
        </div>
      )}

      {(endTimeLabel || dropOffLocation) && (
        <div className={cn("section", ["pickup-section"])}>
          {endTimeLabel && (
            <div className={cn("field")}>
              <div className={cn("label")}>
                {t("activities.postBooking.endTime")}
              </div>
              <p className={cn("value")}>{endTimeLabel}</p>
            </div>
          )}
          {dropOffLocation && (
            <div className={cn("field")}>
              <div className={cn("label")}>
                {t("activities.postBooking.details.dropOffLocation")}
              </div>
              <p className={cn("value")}>{dropOffLocation}</p>
            </div>
          )}
        </div>
      )}

      {hasInclusions && (
        <div className={cn("section", ["inclusions"])}>
          <div className={cn("label")}>
            {t("activities.postBooking.details.inclusions")}
          </div>
          <div className={cn("content")}>
            {inclusions.length > 0 && (
              <ul className={cn("inclusions")}>
                {inclusions.map((inclusion, index) => (
                  <li
                    className={cn("inclusion")}
                    key={`inclusion-${inclusion}-${index}`}
                  >
                    <Icon
                      iconName={IconNameEnum.check}
                      alt={t(
                        "activities.postBooking.bookingQuestions.inclusionAltLabel"
                      )}
                    />
                    {inclusion}
                  </li>
                ))}
              </ul>
            )}
            {exclusions.length > 0 && (
              <ul className={cn("exclusions")}>
                {exclusions.map((exclusion, index) => (
                  <li
                    className={cn("exclusion")}
                    key={`exclusion-${exclusion}-${index}`}
                  >
                    <Icon
                      iconName={IconNameEnum.close}
                      alt={t(
                        "activities.postBooking.bookingQuestions.exclusionAltLabel"
                      )}
                    />
                    {exclusion}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {languageLabel && (
        <div className={cn("section")}>
          <div className={cn("label")}>
            {t("activities.postBooking.details.language")}
          </div>
          <p className={cn("value")}>{languageLabel}</p>
        </div>
      )}

      {specialRequirements && (
        <div className={cn("section")}>
          <div className={cn("label")}>
            {t("activities.postBooking.details.specialRequirements")}
          </div>
          <p className={cn("value")}>{specialRequirements}</p>
        </div>
      )}
    </div>
  );
};
